import * as React from "react";
import { LinksArray } from "../../data/LinkObjects";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import LandscapeRounded from "@mui/icons-material/LandscapeRounded";
import {
  Hiking,
  Home,
  LocalDining,
  MailOutline,
  PhishingOutlined,
} from "@mui/icons-material";
import { Typography, Switch } from "@mui/material";
import { ThemeContext } from "../../App";
import { Link as RouterLink } from "react-router-dom";

const drawerWidth = 180;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
}));

export function NavMenu() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const { darkMode, toggleDarkMode } = React.useContext(ThemeContext);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box component="nav" sx={{ display: "flex" }}>
      <AppBar open={open}>
        <Toolbar component="nav" sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ListItem component="li" sx={{ paddingLeft: "0" }}>
              <ListItemButton
                component={RouterLink}
                to="/"
                onClick={handleDrawerClose}
                sx={{ padding: "0" }}
              >
                <LandscapeRounded
                  sx={{
                    fontSize: "3rem",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : "secondary.main",
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    color: "primary.contrastText",
                    fontWeight: "bold",
                    marginLeft: ".5rem",
                    marginTop: ".675rem",
                    marginBottom: "0",
                    letterSpacing: ".05em",
                    textShadow: "0 2px 8px rgba(157,34,53,0.15)",
                  }}
                >
                  NWA Blog
                </Typography>
              </ListItemButton>
            </ListItem>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              checked={darkMode}
              onChange={toggleDarkMode}
              color="default"
              inputProps={{ "aria-label": "toggle dark mode" }}
              sx={{ mr: 2 }}
            />
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerOpen}
              sx={{ ...(open && { display: "none" }) }}
            >
              <Menu sx={{ fontSize: "2.5rem", color: "accent.main" }} />
            </IconButton>
          </Box>
        </Toolbar>
        <Box sx={{ bgcolor: "accent.main", height: ".5rem" }}></Box>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <DrawerHeader sx={{ backgroundColor: "secondary.main" }}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List component="nav">
          <ListItem disablePadding divider={true}>
            <ListItemButton
              component={RouterLink}
              to="/contact"
              onClick={handleDrawerClose}
            >
              <ListItemIcon>
                <MailOutline sx={{ fontSize: "2rem" }} color="primary" />
              </ListItemIcon>
              <Typography component="h2" sx={{ marginBottom: "0" }}>
                Contact
              </Typography>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding divider={true}>
            <ListItemButton
              component={RouterLink}
              to="/best-of"
              onClick={handleDrawerClose}
            >
              <ListItemIcon>
                <LandscapeRounded sx={{ fontSize: "2rem" }} color="primary" />
              </ListItemIcon>
              <Typography component="h2" sx={{ marginBottom: "0" }}>
                Best Of
              </Typography>
            </ListItemButton>
          </ListItem>
          {LinksArray.map((link, index) => (
            <ListItem key={index} disablePadding divider={true}>
              <ListItemButton
                component={RouterLink}
                to={link.link}
                onClick={handleDrawerClose}
              >
                <ListItemIcon>
                  {link.text === "Home" ? (
                    <Home sx={{ fontSize: "2rem" }} color="primary" />
                  ) : link.text === "Food" ? (
                    <LocalDining sx={{ fontSize: "2rem" }} color="secondary" />
                  ) : link.text === "Trails" ? (
                    <Hiking sx={{ fontSize: "2rem" }} color="primary" />
                  ) : link.text === "Fishing" ? (
                    <PhishingOutlined
                      sx={{ fontSize: "2rem" }}
                      color="secondary"
                    />
                  ) : (
                    ""
                  )}
                </ListItemIcon>
                <Typography component="h2" sx={{ marginBottom: "0" }}>
                  {link.text}
                </Typography>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
      </Drawer>
    </Box>
  );
}
